import React, {useEffect} from 'react';
import Fuse from 'fuse.js';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import {useAsync} from 'react-async';
import {createSelector} from 'reselect';
import useConstant from 'use-constant';
import computeScrollIntoView from 'compute-scroll-into-view';

import IconSelector from '@ibibo/icon-selector';

const fuseOptions = {
  shouldSort: true,
  threshold: 0.3,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  findAllMatches: true,
  tokenize: true,
  matchAllTokens: true,
  keys: ['name'],
};

// APP CONFIG
import {locationAndHotelSearchURL} from '../../../../../../shared/config/app-config';

// COMPONENTS
import Text from '../../../../../core-components/TextFieldExpt/TextFieldExpt';
import CheckBoxList from '../../../common/CheckBoxList/CheckBoxList';

// HOOKS
import useAutosuggest from '../../../../../../shared/hooks/useAutosuggest';
import {ArrayTogglerBehaviorTypes} from '../../../../../../shared/hooks/useArrayToggler';

// ICONS
import SearchInputIcon from '../../../../../Icons/SearchInputIcon';

// DESIGN
import {defaultTheme} from 'build-theme/src/build-theme';
// CONSTANTS
import {DEBOUNCE_TIMEOUT, DEFAULT_ERROR_MESSAGE} from '../constants';

// UTILS and CONSTANTS
import {getSegregatedItems, itemToString} from '../utils';
// STYLES
import * as S from './LocationAndHotelSearch.styles';

import Rating from './Rating';

// TYPES
import {AppState} from '../../../../../../shared/store';

import {IFilters} from '../../../../../../shared/pages/SRP/redux/types';
import {isDefined} from '../../../../../../types/type-guards';
import AutosuggestIconSelector from '../../../../../../shared/components/AutosuggestIconSelector';

const cityIdSelector = createSelector(
  (state: AppState) => state.srp?.srpConfig?.cityVoyagerObj?._id,
  cityId => cityId,
);

const fetchVoyagerLocationAndHotelSuggestions = async (
  {inputValue, cityId}: {inputValue: string; cityId: number},
  {signal},
) => {
  if (!inputValue) {
    return [];
  }
  const fetchVoyagerUrlForInput = locationAndHotelSearchURL({inputValue, cityId});
  const res = await fetch(fetchVoyagerUrlForInput, {signal});
  if (!res.ok) throw new Error(res.statusText);
  const ret = await res.json();

  if (ret.data && ret.data.r) {
    return ret.data.r;
  }

  /** 🐨 handle gracefully if voyager doesn't return ret.data.r an array */
};

type StaticLocationItemType = {
  name: string;
  count: number;
};

type StaticLocationFilterStateItemType = {
  id_type: 'voy';
  loc_id: string;
  n: string;
  // staticFilter: boolean;
};

// overload
function itemsStoreNormalizationFn(
  direction: 'itemsToStore',
  {
    staticLocationItems,
    staticLocationsNamesIdMap,
  }: {
    staticLocationItems: StaticLocationItemType[];
    staticLocationsNamesIdMap: {
      [K: string]: string;
    };
  },
): StaticLocationFilterStateItemType[];

// overload
function itemsStoreNormalizationFn(
  direction: 'storeToItems',
  {
    locationFilterState,
    nameCountMap,
  }: {
    locationFilterState: StaticLocationFilterStateItemType[];
    nameCountMap: {
      [K: string]: number;
    };
  },
): StaticLocationItemType[];

// function itemsStoreNormalizationFn(direction: any, object: object): void

function itemsStoreNormalizationFn(
  direction: 'itemsToStore' | 'storeToItems',
  {
    staticLocationItems = [],
    staticLocationsNamesIdMap = {},
    locationFilterState = [],
    nameCountMap = {},
  }: {
    staticLocationItems?: StaticLocationItemType[];
    staticLocationsNamesIdMap?: {
      [K: string]: string;
    };
    locationFilterState?: StaticLocationFilterStateItemType[];
    nameCountMap?: {
      [K: string]: number;
    };
  },
) {
  if (direction === 'itemsToStore') {
    const id_type: 'voy' = 'voy'; // needed for ts

    const ret = staticLocationItems.map(i => ({
      id_type,
      loc_id: staticLocationsNamesIdMap[i.name],
      n: i.name,
      // staticFilter: true
    }));

    return ret;
  }

  if (direction === 'storeToItems') {
    const ret = locationFilterState
      // .filter( i => i.staticFilter )
      .map(i => ({
        name: i.n,
        count: nameCountMap[i.n],
      }));

    return ret;
  }

  return;
}

function scrollIntoView(node, menuNode) {
  if (node === null) {
    return;
  }

  const actions = computeScrollIntoView(node, {
    boundary: menuNode,
    block: 'start',
    inline: 'start',
  });
  actions.forEach(({el, top, left}) => {
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}

function LocationAndHotelSearch({
  onItemSelection,
  cityId,
  staticFilterLocationsList = [],
  locationFilterState = [],
  updateFilterCaller,
  staticLocationsNamesIdMap = {},
  landmarkOrAreaSelected,
  overrankVhid = '',
  setLandmarkOrAreaSelected,
  resetHotelSelection,
}) {
  // const isStaticLocationsList = !! staticFilterLocationsList.length

  const {
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, getLabelProps},
    state: {isOpen, highlightedIndex, selectedItem, inputValue},
    actions: {openMenu, clearSelection},
  } = useAutosuggest({
    itemToString,
    onSelectedItemChange(changes) {
      onItemSelection(changes);
    },
  });

  // const cityId = useSelector(cityIdSelector);

  const debouncedVoyagerFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchVoyagerLocationAndHotelSuggestions, DEBOUNCE_TIMEOUT),
  );

  const {data: items = [], isPending, isRejected, error} = useAsync({
    promiseFn: debouncedVoyagerFetchPromise,
    watch: inputValue,
    inputValue,
    cityId,
  });

  const {hotelItems = [], locationItems = []} = getSegregatedItems(items);

  const iconOptions = {fill: defaultTheme.colors.grey.grey50, width: '100%', height: '100%'};

  const nameCountMap = React.useMemo(
    () =>
      staticFilterLocationsList.reduce(
        (acc, currentVal) => ({
          ...acc,
          [currentVal.name]: currentVal.count,
        }),
        {},
      ),
    [staticFilterLocationsList],
  );

  const defaultStaticCheckboxState = itemsStoreNormalizationFn('storeToItems', {
    // remove dynamic landmark or area item
    locationFilterState: locationFilterState.filter(item =>
      landmarkOrAreaSelected ? item.loc_ud !== landmarkOrAreaSelected.loc_id : true,
    ),
    nameCountMap,
  });

  const fuse = new Fuse(staticFilterLocationsList, fuseOptions);

  let fuzzySearchResults = [];

  if (inputValue) {
    fuzzySearchResults = fuse.search(inputValue);
  } else {
    fuzzySearchResults = [];
  }

  const checkboxDataList = inputValue ? fuzzySearchResults.slice(0, 15) : staticFilterLocationsList;

  const scrollContainerRef = React.useRef(null);
  const toScrollToRef = React.useRef(null);

  /**
   * when there's no input (when the user clears)
   * scroll to top of the menu because the user
   * might be down into the scroll
   */

  // useEffect(() => {
  //   if (Array.isArray(locationFilterState)) {
  //     if (overrankVhid && locationFilterState.length) {
  //       /* when a hotel is already selected and new location filter/selection is applied */
  //       resetHotelSelection();
  //       if (selectedItem && selectedItem.t == 'hotel') clearSelection();
  //     } else {
  //       selectedItem &&
  //         !overrankVhid &&
  //         locationFilterState.filter(item => item?.loc_id === selectedItem?._id).length === 0 &&
  //         clearSelection();
  //     }

  //     if (landmarkOrAreaSelected) {
  //       locationFilterState.filter(item => item?.loc_id === landmarkOrAreaSelected?.loc_id)
  //         .length == 0 && setLandmarkOrAreaSelected(null);
  //     }
  //   }
  // }, [locationFilterState]);

  const onClearButtonClick = () => {
    if (overrankVhid) {
      resetHotelSelection();
      if (selectedItem && selectedItem.t == 'hotel') clearSelection();
      updateFilterCaller();
    } else if (landmarkOrAreaSelected && selectedItem) {
      let newFilters = locationFilterState.filter(
        item => item?.loc_id !== landmarkOrAreaSelected?.loc_id,
      );
      updateFilterCaller({location_search: newFilters});
      setLandmarkOrAreaSelected(null);
    }
  };
  
  useEffect(() => {
    scrollIntoView(toScrollToRef.current, scrollContainerRef.current);
  }, [inputValue]);

  return (
    <S.AutocompleteWrapper {...getRootProps()}>
      {/* info: this label is hidden */}
      <S.SearchLabel {...getLabelProps()}>Search Location Or Hotel Name</S.SearchLabel>
      <S.InputWrapper>
        <S.SearchInput
          {...getInputProps({
            placeholder: 'Any area, address or property',
            onFocus() {
              if (!!checkboxDataList.length) {
                openMenu();
              }
            },
          })}
        />
        {inputValue && selectedItem ? 
          <div onClick={onClearButtonClick}>
            <IconSelector iconKey="close" props={{
              width: '1rem',
              height: '1rem',
              fill: defaultTheme.colors.grey.grey50
            }} />
          </div> : <SearchInputIcon />
          // <CloseIcon
          //   width=".8rem"
          //   height=".8rem"
          //   fill={defaultTheme.colors.grey.grey75}
            
          // />
        }
      </S.InputWrapper>

      {isRejected && (error || DEFAULT_ERROR_MESSAGE) && (
        <S.ErrorMessage>
          <Text align="center" bold customFontSize="1">
            {error}
          </Text>
        </S.ErrorMessage>
      )}

      <S.Menu
        {...getMenuProps({
          ref: scrollContainerRef,
        })}
      >
        {isOpen ? (
          <div>
            <S.ScrollToTopWhenNoInputHiddenElement ref={toScrollToRef} />
            {!!checkboxDataList.length && (
              <S.MidListItemSegregationLabel>Show Hotels In</S.MidListItemSegregationLabel>
            )}
            <div
            // style={{
            //   padding: '0 1rem 0 1rem',
            // }}
            >
              <S.StaticLocationsCheckboxesWrapper>
                {!!checkboxDataList.length && (
                  <CheckBoxList
                    name="Static Locations Filter List"
                    data={checkboxDataList}
                    lookup={location => location.name}
                    type={ArrayTogglerBehaviorTypes.multiSelect}
                    isSingleFilter={true}
                    contentsWrapperStyles={{
                      width: '100%',
                    }}
                    actionHandler={(
                      items: Array<{
                        name: string;
                        count: number;
                      }>,
                    ) => {
                      const staticLocationItems = items.filter(item =>
                        landmarkOrAreaSelected ? isDefined(item.count) : true,
                      );

                      const everyLocationNameHasVoyagerLocationId = staticLocationItems.every(
                        item => !!staticLocationsNamesIdMap[item.name],
                      );

                      /**
                       * this is assuming that there's some mismatch by data hermes sent us
                       * this is being extra safe, and might not be needed
                       *
                       * if it's needed:
                       * 🐨 intimate to user that something went wrong
                       * 🐨 ask Abinash about how to prevent checked state of checkbox ( keeping it controlled )
                       */

                      if (!everyLocationNameHasVoyagerLocationId || !updateFilterCaller) {
                        return;
                      }

                      const storeStateStaticLocationItems = itemsStoreNormalizationFn(
                        'itemsToStore',
                        {
                          staticLocationItems: staticLocationItems,
                          staticLocationsNamesIdMap,
                        },
                      );

                      let storeStaticLocationItems = landmarkOrAreaSelected
                        ? storeStateStaticLocationItems.concat(landmarkOrAreaSelected)
                        : storeStateStaticLocationItems;

                      updateFilterCaller({location_search: storeStaticLocationItems});
                    }}
                    defaultState={defaultStaticCheckboxState || []}
                    renderItem={location => (
                      <S.StaticLocationItemWrapper key={location.name}>
                        <S.StaticLocationName>{location.name}</S.StaticLocationName>

                        <S.StaticLocationCount>{location.count}</S.StaticLocationCount>
                      </S.StaticLocationItemWrapper>
                    )}
                  />
                )}
              </S.StaticLocationsCheckboxesWrapper>
            </div>

            {!!hotelItems.length && (
              <S.MidListItemSegregationLabel>Hotels</S.MidListItemSegregationLabel>
            )}
            {hotelItems.map((item, index) => {
              const key = `${item._id}-${item.n ? item.n : ''}-${index}` || item.n || index;

              return (
                <S.SearchListItem
                  key={key}
                  {...getItemProps({
                    item,
                    index,
                    isActive: highlightedIndex === index,
                    isSelected: selectedItem === item,
                    noBorder: index === hotelItems.length - 1,
                  })}
                >
                  <S.IconWrapperDiv>
                    <AutosuggestIconSelector name={item.t} {...iconOptions} />
                  </S.IconWrapperDiv>
                  <S.SearchListItemText style={{flex: 3}}>
                    <span>{itemToString(item)}</span>
                  </S.SearchListItemText>
                  <S.RatingWrapper>
                    <Rating item={item} />
                  </S.RatingWrapper>
                </S.SearchListItem>
              );
            })}

            {!!locationItems.length && (
              <S.MidListItemSegregationLabel>Show Hotels Nearby</S.MidListItemSegregationLabel>
            )}

            {locationItems.map((item, i) => {
              const index = i + hotelItems.length;

              const key = `${item._id}-${item.n ? item.n : ''}-${index}` || item.n || index;

              return (
                <S.SearchListItem
                  key={key}
                  {...getItemProps({
                    item,
                    index,
                    isActive: highlightedIndex === index,
                    isSelected: selectedItem === item,
                  })}
                >
                  <S.IconWrapperDiv>
                    <AutosuggestIconSelector name={item.t} {...iconOptions} />
                  </S.IconWrapperDiv>
                  <S.SearchListItemText>
                    <span>{itemToString(item)}</span>
                  </S.SearchListItemText>
                  <S.RatingWrapper>
                    <Rating item={item} />
                  </S.RatingWrapper>
                </S.SearchListItem>
              );
            })}
          </div>
        ) : null}
      </S.Menu>
    </S.AutocompleteWrapper>
  );
}

export default LocationAndHotelSearch;
